import type { Root } from './prototypeTypes';

export interface AudioPlaybackTarget {
	topicId: string;
	chapterIndex: number;
}

interface Flight<T> {
	target: AudioPlaybackTarget;
	desiredPlaying: boolean;
	promise: Promise<T>;
}

const flights = new WeakMap<object, Flight<unknown>>();

function sameTarget(a: AudioPlaybackTarget, b: AudioPlaybackTarget): boolean {
	return a.topicId === b.topicId && a.chapterIndex === b.chapterIndex;
}

/**
 * One browser root can have exactly one token request for a target. Subsequent
 * taps do not cancel it: they merely replace the desired final play/pause
 * state, while the UI remains optimistic for every tap.
 */
export function startAudioPlaybackSingleFlight<T>(
	root: Root,
	target: AudioPlaybackTarget,
	desiredPlaying: boolean,
	run: (desiredPlaying: () => boolean) => Promise<T>,
): { started: boolean; desiredPlaying: boolean; isDesiredPlaying: () => boolean; promise: Promise<T> } {
	const key = root as unknown as object;
	const active = flights.get(key);
	if (active && sameTarget(active.target, target)) {
		active.desiredPlaying = desiredPlaying;
		return { started: false, desiredPlaying, isDesiredPlaying: () => active.desiredPlaying, promise: active.promise as Promise<T> };
	}

	const flight = { target, desiredPlaying, promise: undefined as unknown as Promise<T> };
	// Invoke the runner in the user-gesture call stack. It can start a media
	// request immediately, while subsequent taps still see the stored flight.
	flights.set(key, flight);
	const promise = Promise.resolve(run(() => flight.desiredPlaying));
	flight.promise = promise;
	void promise.finally(() => {
		if (flights.get(key) === flight) flights.delete(key);
	});
	return { started: true, desiredPlaying, isDesiredPlaying: () => flight.desiredPlaying, promise };
}

/** Switch only the final desired state when the same target is still loading. */
export function toggleAudioPlaybackSingleFlight(root: Root, target: AudioPlaybackTarget): boolean | null {
	const active = flights.get(root as unknown as object);
	if (!active || !sameTarget(active.target, target)) return null;
	active.desiredPlaying = !active.desiredPlaying;
	return active.desiredPlaying;
}

export function hasAudioPlaybackSingleFlight(root: Root, target: AudioPlaybackTarget): boolean {
	const active = flights.get(root as unknown as object);
	return Boolean(active && sameTarget(active.target, target));
}

export function __resetAudioPlaybackSingleFlightForTest(root: Root): void {
	flights.delete(root as unknown as object);
}
